import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { 
  EmpresaInfo, 
  DetalleRecibo, 
  DetalleVentaLote,
  DetalleComision,
  DetalleMovimiento,
  DetalleAbono,
  DetalleDevolucion,
  formatCurrency, 
  formatDate,
  getTituloRecibo
} from './receiptTemplates';

/**
 * Genera un PDF de recibo
 * @param empresa Información de la empresa
 * @param detalle Detalles del recibo según el tipo
 * @returns Promise<Blob> El PDF generado como Blob
 */
export async function generateReceiptPDF(
  empresa: EmpresaInfo,
  detalle: DetalleRecibo
): Promise<Blob> {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  let yPosition = 20;

  // ============ ENCABEZADO: LOGO Y DATOS DE LA EMPRESA ============
  
  // Logo (si existe)
  if (empresa.logoUrl) {
    try {
      // Cargar logo como imagen
      const img = new Image();
      img.src = empresa.logoUrl;
      await new Promise((resolve) => {
        img.onload = resolve;
      });
      doc.addImage(img, 'PNG', 15, yPosition, 30, 30);
    } catch (error) {
      console.warn('No se pudo cargar el logo:', error);
    }
  }

  // Información de la empresa (centrada o al lado del logo)
  const empresaStartX = empresa.logoUrl ? 50 : pageWidth / 2;
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(empresa.razonSocial, empresaStartX, yPosition, { align: empresa.logoUrl ? 'left' : 'center' });
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  yPosition += 5;
  
  if (empresa.nit) {
    doc.text(`NIT: ${empresa.nit}`, empresaStartX, yPosition, { align: empresa.logoUrl ? 'left' : 'center' });
    yPosition += 4;
  }
  
  doc.text(empresa.direccionCompleta, empresaStartX, yPosition, { align: empresa.logoUrl ? 'left' : 'center' });
  yPosition += 4;
  
  const telefonos = empresa.telefono2 
    ? `Tel: ${empresa.telefono1} / ${empresa.telefono2}`
    : `Tel: ${empresa.telefono1}`;
  doc.text(telefonos, empresaStartX, yPosition, { align: empresa.logoUrl ? 'left' : 'center' });
  yPosition += 4;
  
  if (empresa.email) {
    doc.text(`Email: ${empresa.email}`, empresaStartX, yPosition, { align: empresa.logoUrl ? 'left' : 'center' });
    yPosition += 4;
  }

  yPosition += 10; // Espacio después del encabezado

  // ============ LÍNEA DIVISORIA ============
  doc.setDrawColor(200, 200, 200);
  doc.line(15, yPosition, pageWidth - 15, yPosition);
  yPosition += 10;

  // ============ TÍTULO DEL RECIBO ============
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  const titulo = getTituloRecibo(detalle.tipoRecibo);
  doc.text(titulo, pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 8;

  // Número de recibo y fecha
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`No. Recibo: ${detalle.numeroRecibo}`, 15, yPosition);
  doc.text(`Fecha emisión: ${formatDate(detalle.fechaEmision)}`, pageWidth - 15, yPosition, { align: 'right' });
  yPosition += 10;

  // ============ LÍNEA DIVISORIA ============
  doc.line(15, yPosition, pageWidth - 15, yPosition);
  yPosition += 8;

  // ============ CONTENIDO ESPECÍFICO SEGÚN TIPO DE RECIBO ============
  switch (detalle.tipoRecibo) {
    case 'venta_lote':
      yPosition = generateVentaLoteContent(doc, detalle as DetalleVentaLote, yPosition);
      break;
    case 'pago_comision':
      yPosition = generateComisionContent(doc, detalle as DetalleComision, yPosition);
      break;
    case 'movimiento':
      yPosition = generateMovimientoContent(doc, detalle as DetalleMovimiento, yPosition);
      break;
    case 'abono':
      yPosition = generateAbonoContent(doc, detalle as DetalleAbono, yPosition);
      break;
    case 'devolucion':
      yPosition = generateDevolucionContent(doc, detalle as DetalleDevolucion, yPosition);
      break;
  }

  // ============ PIE DE PÁGINA ============
  yPosition += 20;
  doc.setDrawColor(200, 200, 200);
  doc.line(15, yPosition, pageWidth - 15, yPosition);
  yPosition += 8;
  
  doc.setFontSize(8);
  doc.setFont('helvetica', 'italic');
  doc.text('Este es un comprobante interno sin valor fiscal.', pageWidth / 2, yPosition, { align: 'center' });
  yPosition += 5;
  const fechaGeneracion = new Date().toLocaleString('es-GT');
  doc.text(`Generado: ${fechaGeneracion}`, pageWidth / 2, yPosition, { align: 'center' });

  // Convertir a Blob
  return doc.output('blob');
}

// ============ FUNCIONES PARA CONTENIDO ESPECÍFICO ============

function generateVentaLoteContent(doc: jsPDF, detalle: DetalleVentaLote, yPosition: number): number {
  const pageWidth = doc.internal.pageSize.width;
  
  // DATOS DEL CLIENTE
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('DATOS DEL CLIENTE', 15, yPosition);
  yPosition += 6;
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Nombre: ${detalle.cliente.nombre}`, 15, yPosition);
  yPosition += 5;
  if (detalle.cliente.dpi) {
    doc.text(`DPI: ${detalle.cliente.dpi}`, 15, yPosition);
    yPosition += 5;
  }
  if (detalle.cliente.telefono) {
    doc.text(`Teléfono: ${detalle.cliente.telefono}`, 15, yPosition);
    yPosition += 5;
  }
  
  yPosition += 5;
  
  // DETALLE DE LA TRANSACCIÓN
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('DETALLE DE LA TRANSACCIÓN', 15, yPosition);
  yPosition += 6;
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  
  const detalles = [
    ['Concepto:', `Venta de Lote(s) ${detalle.lotes}`],
    ['Vendedor:', detalle.vendedor],
    ['Fecha de Venta:', formatDate(detalle.fechaVenta)],
    ['Tipo de Pago:', detalle.tipoPago === 'contado' ? 'Contado' : 'Crédito'],
    ['Valor Total:', formatCurrency(detalle.valorTotal)]
  ];
  
  if (detalle.tipoPago === 'pagos') {
    detalles.push(
      ['Enganche:', formatCurrency(detalle.enganche || 0)],
      ['Número de Cuotas:', String(detalle.numeroCuotas || 0)],
      ['Monto por Cuota:', formatCurrency(detalle.montoCuota || 0)]
    );
  }
  
  if (detalle.boletaDeposito) {
    detalles.push(['Boleta Depósito:', detalle.boletaDeposito]);
  }
  
  if (detalle.reciboCaja) {
    detalles.push(['Recibo de Caja:', detalle.reciboCaja]);
  }
  
  autoTable(doc, {
    startY: yPosition,
    head: [],
    body: detalles,
    theme: 'plain',
    styles: { fontSize: 9, cellPadding: 2 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 40 },
      1: { cellWidth: 'auto' }
    }
  });
  
  return (doc as any).lastAutoTable.finalY + 10;
}

function generateComisionContent(doc: jsPDF, detalle: DetalleComision, yPosition: number): number {
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('DATOS DEL VENDEDOR', 15, yPosition);
  yPosition += 6;
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Nombre: ${detalle.vendedor.nombre}`, 15, yPosition);
  yPosition += 10;
  
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('DETALLE DEL PAGO DE COMISIÓN', 15, yPosition);
  yPosition += 6;
  
  const detalles = [
    ['Lote(s) Vendido(s):', detalle.lotes],
    ['Comprador:', detalle.comprador],
    ['Fecha Venta Original:', formatDate(detalle.fechaVentaOriginal)],
    ['Monto Comisión:', formatCurrency(detalle.montoComision)],
    ['Fecha de Pago:', formatDate(detalle.fechaPago)],
    ['Transferencia:', detalle.transferencia],
    ['Boleta:', detalle.boleta]
  ];
  
  autoTable(doc, {
    startY: yPosition,
    head: [],
    body: detalles,
    theme: 'plain',
    styles: { fontSize: 9, cellPadding: 2 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 50 },
      1: { cellWidth: 'auto' }
    }
  });
  
  return (doc as any).lastAutoTable.finalY + 10;
}

function generateMovimientoContent(doc: jsPDF, detalle: DetalleMovimiento, yPosition: number): number {
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  const tipoTexto = detalle.tipo === 'ingreso' ? 'INGRESO' : 'EGRESO';
  doc.text(`DETALLE DEL ${tipoTexto}`, 15, yPosition);
  yPosition += 6;
  
  const detalles = [
    ['Concepto:', detalle.concepto],
    ['Responsable:', detalle.responsable],
    ['Fecha:', formatDate(detalle.fecha)],
    ['Monto:', formatCurrency(detalle.monto)]
  ];
  
  if (detalle.referencia) {
    detalles.push(['Referencia:', detalle.referencia]);
  }
  
  if (detalle.lotes) {
    detalles.push(['Lotes:', detalle.lotes]);
  }
  
  if (detalle.vendedor) {
    detalles.push(['Vendedor:', detalle.vendedor]);
  }
  
  if (detalle.comprador) {
    detalles.push(['Comprador:', detalle.comprador]);
  }
  
  autoTable(doc, {
    startY: yPosition,
    head: [],
    body: detalles,
    theme: 'plain',
    styles: { fontSize: 9, cellPadding: 2 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 40 },
      1: { cellWidth: 'auto' }
    }
  });
  
  return (doc as any).lastAutoTable.finalY + 10;
}

function generateAbonoContent(doc: jsPDF, detalle: DetalleAbono, yPosition: number): number {
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('DATOS DEL CLIENTE', 15, yPosition);
  yPosition += 6;
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Nombre: ${detalle.cliente.nombre}`, 15, yPosition);
  yPosition += 5;
  if (detalle.cliente.dpi) {
    doc.text(`DPI: ${detalle.cliente.dpi}`, 15, yPosition);
    yPosition += 5;
  }
  
  yPosition += 5;
  
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('DETALLE DEL ABONO', 15, yPosition);
  yPosition += 6;
  
  const detalles = [
    ['Lote(s):', detalle.lotes]
  ];
  
  if (detalle.cuotaNumero) {
    detalles.push(['Cuota No.:', String(detalle.cuotaNumero)]);
  }
  
  if (detalle.fechaVencimiento) {
    detalles.push(['Fecha Vencimiento:', formatDate(detalle.fechaVencimiento)]);
  }
  
  detalles.push(
    ['Monto Abono:', formatCurrency(detalle.montoAbono)],
    ['Fecha de Pago:', formatDate(detalle.fechaPago)],
    ['Transferencia:', detalle.transferencia],
    ['Boleta:', detalle.boleta]
  );
  
  if (detalle.saldoPendiente !== undefined) {
    detalles.push(['Saldo Pendiente:', formatCurrency(detalle.saldoPendiente)]);
  }
  
  autoTable(doc, {
    startY: yPosition,
    head: [],
    body: detalles,
    theme: 'plain',
    styles: { fontSize: 9, cellPadding: 2 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 45 },
      1: { cellWidth: 'auto' }
    }
  });
  
  return (doc as any).lastAutoTable.finalY + 10;
}

function generateDevolucionContent(doc: jsPDF, detalle: DetalleDevolucion, yPosition: number): number {
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('DATOS DEL CLIENTE', 15, yPosition);
  yPosition += 6;
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Nombre: ${detalle.cliente.nombre}`, 15, yPosition);
  yPosition += 5;
  if (detalle.cliente.dpi) {
    doc.text(`DPI: ${detalle.cliente.dpi}`, 15, yPosition);
    yPosition += 5;
  }
  
  yPosition += 5;
  
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('DETALLE DE LA DEVOLUCIÓN', 15, yPosition);
  yPosition += 6;
  
  const detalles = [
    ['Lote(s):', detalle.lotes],
    ['Vendedor:', detalle.vendedor],
    ['Fecha Solicitud:', formatDate(detalle.fechaSolicitud)],
    ['Motivo:', detalle.motivo],
    ['Monto Pagado:', formatCurrency(detalle.montoPagado)],
    ['Penalización (' + detalle.porcentajePenalizacion + '%):', formatCurrency(detalle.penalizacion)],
    ['Monto Devuelto:', formatCurrency(detalle.montoDevuelto)],
    ['Banco:', detalle.banco],
    ['Beneficiario:', detalle.beneficiario],
    ['Boleta Transferencia:', detalle.boletaTransferencia]
  ];
  
  autoTable(doc, {
    startY: yPosition,
    head: [],
    body: detalles,
    theme: 'plain',
    styles: { fontSize: 9, cellPadding: 2 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 50 },
      1: { cellWidth: 'auto' }
    }
  });
  
  return (doc as any).lastAutoTable.finalY + 10;
}

/**
 * Descarga un PDF de recibo
 * @param blob El blob del PDF generado
 * @param fileName Nombre del archivo (sin extensión)
 */
export function downloadReceiptPDF(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${fileName}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}


